import { useState } from "react";
import { classNames } from "primereact/utils";
import { Icon, IconType } from "../../icons/Icon";
import { dateUtility } from "../../infrastructure/utilities/dateUtility";
import styles from "./WorkoutRow.module.scss";

export interface IWorkoutRowExercisePreview {
  exerciseName: string;
  setCount: number;
}

export interface IWorkoutRowData {
  /** Display title — the workout name, falling back to the event name. */
  title: string;
  /** ISO date string of the workout. */
  date: string;
  totalVolumeKg: number;
  exerciseCount: number;
  /**
   * Every exercise of the workout, in its order. The card previews the first few and
   * expands to the rest on demand.
   */
  exercises: IWorkoutRowExercisePreview[];
  /** 1-based position in the workout's lineage; shown as a "#N" badge when present. */
  lineageNumber?: number | null;
}

interface IWorkoutRowProps {
  workout: IWorkoutRowData;
  onClick?: () => void;
}

/** How many exercises a collapsed card previews before "+ още N упражнения". */
export const PREVIEW_EXERCISE_LIMIT = 3;

const COLLAPSE_LABEL = "Скрий";

const formatVolume = (kg: number): string => {
  if (!kg || kg <= 0) return "—";
  return `${Math.round(kg).toLocaleString("bg-BG")} кг`;
};

const setsLabel = (count: number): string =>
  `${count} ${count === 1 ? "серия" : "серии"}`;

const moreLabel = (count: number): string =>
  `+ още ${count} ${count === 1 ? "упражнение" : "упражнения"}`;

/**
 * Reusable summary card for a single workout: title, date, a stat strip
 * (volume · exercises) and an exercise preview. The card previews the first three
 * exercises; "+ още N упражнения" expands it in place to every exercise so the whole
 * workout can be read without opening it, and "Скрий" folds it back. The expand
 * button is a sibling of the open-workout button, never nested inside it, so toggling
 * the preview cannot open the workout and the markup stays valid. Each card keeps its
 * own expanded state. Backs the "My Workouts" page and the admin user-profile list.
 */
export const WorkoutRow = ({ workout, onClick }: IWorkoutRowProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Only names that actually came down can be revealed, so the count is taken from
  // the list, not from exerciseCount.
  const hiddenCount = Math.max(
    0,
    workout.exercises.length - PREVIEW_EXERCISE_LIMIT,
  );
  const canExpand = hiddenCount > 0;
  const visibleExercises = isExpanded
    ? workout.exercises
    : workout.exercises.slice(0, PREVIEW_EXERCISE_LIMIT);

  return (
    <div className={styles.row}>
      <button
        type="button"
        className={classNames(styles.open, {
          [styles.open_with_toggle]: canExpand,
        })}
        onClick={onClick}
        aria-label={`Отвори тренировка ${workout.title}`}
      >
        <div className={styles.head}>
          <div className={styles.head_main}>
            <span className={styles.title}>{workout.title}</span>
            {workout.lineageNumber ? (
              <span className={styles.lineage}>#{workout.lineageNumber}</span>
            ) : null}
          </div>
          <span className={styles.date}>
            {dateUtility.formatDateTime(workout.date)}
          </span>
        </div>

        <div className={styles.stats}>
          <div className={styles.stat}>
            <Icon
              type={IconType.Dumbell}
              width={14}
              height={14}
              color="rgba(160, 158, 220, 0.95)"
            />
            <span className={styles.stat_value}>
              {formatVolume(workout.totalVolumeKg)}
            </span>
          </div>
          <div className={styles.stat}>
            <span className={styles.stat_value}>{workout.exerciseCount}</span>
            <span className={styles.stat_label}>упражнения</span>
          </div>
        </div>

        {visibleExercises.length > 0 && (
          <div className={styles.preview}>
            {visibleExercises.map((exercise, index) => (
              <div
                className={styles.preview_item}
                key={`${exercise.exerciseName}-${index}`}
              >
                <span className={styles.preview_icon}>
                  <Icon
                    type={IconType.Dumbell}
                    width={13}
                    height={13}
                    color="rgba(150, 149, 210, 0.9)"
                  />
                </span>
                <span className={styles.preview_sets}>
                  {setsLabel(exercise.setCount)}
                </span>
                <span className={styles.preview_name}>
                  {exercise.exerciseName}
                </span>
              </div>
            ))}
          </div>
        )}
      </button>

      {canExpand && (
        <button
          type="button"
          className={styles.preview_more}
          onClick={() => setIsExpanded((value) => !value)}
          aria-expanded={isExpanded}
        >
          {isExpanded ? COLLAPSE_LABEL : moreLabel(hiddenCount)}
        </button>
      )}
    </div>
  );
};
